import { useState, useCallback, useMemo } from "react";

const getColumnConfig = (key, columns) => {
  const [colIdx, subIdx] = key.split("-").map(Number);
  return columns[colIdx]?.subColumns[subIdx] || null;
};

export const useColumnWidths = (columns) => {
  const initialWidths = useMemo(() => {
    const widths = {};
    columns.forEach((col, colIdx) => {
      col.subColumns.forEach((subCol, subIdx) => {
        const key = `${colIdx}-${subIdx}`;
        widths[key] = subCol.width || subCol.minWidth || 120;
      });
    });
    return widths;
  }, [columns]);

  const [columnWidths, setColumnWidths] = useState(initialWidths);

  const updateColumnWidth = useCallback((key, newWidth, isMainColumn = false) => {
    setColumnWidths((prev) => {
      if (isMainColumn) {
        // Resizing main column - distribute change proportionally to sub-columns
        // Extract column index from key (key format is "main-0", "main-1", etc.)
        const colIdx = parseInt(key.replace("main-", ""), 10);
        const mainCol = columns[colIdx];
        if (!mainCol) return prev;

        // Get current sub-columns for this main column
        const subKeys = [];
        const subWidths = [];
        const subMinWidths = [];
        let totalCurrentWidth = 0;

        mainCol.subColumns.forEach((subCol, subIdx) => {
          const subKey = `${colIdx}-${subIdx}`;
          subKeys.push(subKey);
          const currentWidth = prev[subKey] || subCol.width || subCol.minWidth || 120;
          subWidths.push(currentWidth);
          subMinWidths.push(subCol.minWidth || 5);
          totalCurrentWidth += currentWidth;
        });

        if (totalCurrentWidth === 0) return prev;

        const widthDiff = newWidth - totalCurrentWidth;
        const newWidths = { ...prev };

        // Distribute the change proportionally, respecting min-widths
        const proportionalChanges = subWidths.map((w) => {
          const proportion = w / totalCurrentWidth;
          return widthDiff * proportion;
        });

        // Apply changes while respecting min-widths
        subKeys.forEach((subKey, i) => {
          const newWidth = Math.max(subMinWidths[i], subWidths[i] + proportionalChanges[i]);
          newWidths[subKey] = newWidth;
        });

        // If we hit min-width constraints, redistribute remaining difference
        const actualTotal = subKeys.reduce((sum, key) => sum + (newWidths[key] || 0), 0);
        const remainingAdjustment = newWidth - actualTotal;

        if (Math.abs(remainingAdjustment) > 0.1) {
          // Redistribute among columns that can still grow/shrink
          const adjustableIndices = [];
          subKeys.forEach((subKey, i) => {
            const currentWidth = newWidths[subKey];
            if (remainingAdjustment > 0 || currentWidth > subMinWidths[i]) {
              adjustableIndices.push(i);
            }
          });

          if (adjustableIndices.length > 0) {
            const perColumnAdjustment = remainingAdjustment / adjustableIndices.length;
            adjustableIndices.forEach((i) => {
              const subKey = subKeys[i];
              newWidths[subKey] = Math.max(
                subMinWidths[i],
                (newWidths[subKey] || 0) + perColumnAdjustment
              );
            });
          }
        }

        return newWidths;
      } else {
        // Resizing sub-column directly
        const config = getColumnConfig(key, columns);
        if (config) {
          const minWidth = config.minWidth || 5;
          const maxWidth = config.maxWidth || 1000;
          const clampedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
          return { ...prev, [key]: clampedWidth };
        }
        return prev;
      }
    });
  }, [columns]);

  return [columnWidths, updateColumnWidth];
};

